import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Tabs, Tab, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, TextField, Button, FormControl,
  InputLabel, Select, MenuItem, Switch, FormControlLabel, Avatar,
  Alert, CircularProgress, Chip, IconButton, Divider, Autocomplete,
  Card, CardContent, Paper, Snackbar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, FormHelperText,
} from '@mui/material';
import {
  Business as BusinessIcon, Add as AddIcon, Edit as EditIcon,
  Delete as DeleteIcon, Save as SaveIcon, LocalOffer as LocalOfferIcon,
  ShoppingCart as ShoppingCartIcon, Settings as SettingsIcon,
  FileDownload as ExportIcon, Link as LinkIcon, Phone as PhoneIcon,
  Tablet as TabletIcon, Computer as ComputerIcon, Article as ArticleIcon,
  Schedule as ScheduleIcon, Close as CloseIcon, Category as CategoryIcon,
  ViewModule as TemplateIcon, TrendingUp as UsageIcon,
  Visibility as PreviewIcon, Create as CreateIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import brandService from '../../services/brandService';
import { getOffers, createOffer, updateOffer, deleteOffer, setOfferPaused } from '../../services/offerService';
import categoryService from '../../services/categoryService';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BrandsContext } from '../BrandsContext';
import BrandListTab from './tabs/BrandListTab';
import BrandSetupTab from './tabs/BrandSetupTab';
import OfferSetupTab from './tabs/OfferSetupTab';
import OfferElementSetupTab from './tabs/OfferElementSetupTab';
import { NO_EXPIRY_DATE } from '../../utils/offerDefaults';

const StyledCard = styled(Card)(({ theme, expired }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  opacity: expired ? 0.7 : 1,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  textTransform: 'capitalize',
  fontWeight: 'bold',
}));

// URL validation helper function
const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (error) {
    return false;
  }
};


const Brands = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // Start with Brand List tab active
  const [brandForm, setBrandForm] = useState({
    networkBrand: '',
    brandName: '',
    company: '',
    contactName: '',
    contactEmail: '',
    addressField1: '',
    addressField2: '',
    country: '',
    stateProvince: '',
    city: '',
    websiteHomepage: '',
    tiktok: '',
    instagram: '',
    facebook: '',
    status: true
  });

  // Dynamic brands state
  const [existingBrands, setExistingBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingBrand, setEditingBrand] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);

  // Offers state

  const [offerLoading, setOfferLoading] = useState(false);
  const [offerError, setOfferError] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [offerForm, setOfferForm] = useState({
    title: '',
    payout: '',
    typeOfAction: '',
    // "Pays on" doubles as the payout-method switch: selecting "Revenue
    // Share" here (vs. Approval/Conversion/Click/Lead/Sale) is what sets
    // Offer.payoutMethod to 'revenue_share' server-side — see
    // handleCreateOffer's `isRevenueShare` derivation below. Distinct from
    // the free-text `payout` field above, which is display-only and never
    // read by postback logic.
    paysOn: '',
    revenueSharePercent: '',
    purchaseUrl: '',
    description: '',
    category: '',
    subcategoryId: '',
    problemSolved: '',
    countriesAccepted: [],
    trafficSources: [],
    trackingType: '',
    brand: '',
    expirationDate: NO_EXPIRY_DATE,
    published: true
  });
  const [editingOffer, setEditingOffer] = useState(null);
  const [editOfferDialogOpen, setEditOfferDialogOpen] = useState(false);
  const [deleteOfferDialogOpen, setDeleteOfferDialogOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState(null);

  // Available offers for brand setup
  const [availableOffers, setAvailableOffers] = useState([]);
  const [availableOffersLoading, setAvailableOffersLoading] = useState(false);

  // Expanded rows for brand list
  const [expandedBrands, setExpandedBrands] = useState(new Set());
  const [brandOffers, setBrandOffers] = useState({});

  // Shared state for template editor
  const [sharedElements, setSharedElements] = useState([]);
  const [sharedSelectedElement, setSharedSelectedElement] = useState(null);
  const [sharedSelectedElementContext, setSharedSelectedElementContext] = useState(null);
  const [sharedSelectedCategory, setSharedSelectedCategory] = useState('Headline/Subheadline');
  const [sharedSelectedSubtype, setSharedSelectedSubtype] = useState(null);
  const [sharedSelectedBrand, setSharedSelectedBrand] = useState('');
  const [sharedSelectedOffer, setSharedSelectedOffer] = useState('');

  // New shared pages state for multi-page support
  const [sharedPages, setSharedPages] = useState([{ id: 1, elements: [] }]);
  const [sharedSelectedTemplateId, setSharedSelectedTemplateId] = useState(null);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await brandService.getAllBrands();
      setExistingBrands(response.brands || []);
    } catch (err) {
      setError('Failed to load brands');
      console.error('Error fetching brands:', err);
    } finally {
      setLoading(false);
    }
  };



  const fetchCategories = async () => {
    try {
      const data = await categoryService.getAllCategories();
      console.log('Fetched categories:', data.categories); // Debug log
      setCategories(data.categories || []);
      return data.categories || [];
    } catch (err) {
      console.error('Error fetching categories:', err);
      return [];
    }
  };

  const fetchAvailableOffers = useCallback(async () => {
    try {
      setAvailableOffersLoading(true);
      // Fetch all offers, including those without brands
      const data = await getOffers(1, 200, false, null, null, null);
      setAvailableOffers(data.offers || []);
    } catch (err) {
      console.error('Error fetching available offers:', err);
      setAvailableOffers([]);
    } finally {
      setAvailableOffersLoading(false);
    }
  }, []);

  const handleToggleBrandOffers = async (brandId) => {
    const newExpanded = new Set(expandedBrands);

    if (expandedBrands.has(brandId)) {
      // Collapse - remove from expanded set
      newExpanded.delete(brandId);
      setExpandedBrands(newExpanded);
    } else {
      // Expand - add to expanded set and fetch offers if not already cached
      newExpanded.add(brandId);
      setExpandedBrands(newExpanded);

      if (!brandOffers[brandId]) {
        try {
          const { getOffersByBrandAdmin } = await import('../../services/offerService');
          const response = await getOffersByBrandAdmin(brandId, 1, 100, true); // Include expired offers
          setBrandOffers(prev => ({
            ...prev,
            [brandId]: response.offers || []
          }));
        } catch (error) {
          console.error('Error fetching brand offers:', error);
          setBrandOffers(prev => ({
            ...prev,
            [brandId]: []
          }));
        }
      }
    }
  };

  // Load brands on component mount
  useEffect(() => {
    fetchBrands();
  }, []);

  // Fetch offers when relevant state changes
  useEffect(() => {
    if (activeTab === 2) { // Only fetch categories when on Offer Setup tab
      fetchCategories();
    }
  }, [activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Fetch categories when switching to Offer Setup tab
    if (newValue === 2) {
      fetchCategories();
    }
  };

  // Offer handlers
  const handleOfferFormChange = (field) => (event) => {
    const value = event.target.value;
    console.log(`Updating field ${field} with value:`, value); // Debug log
    setOfferForm(prev => {
      const updated = { ...prev, [field]: value };
      console.log('Updated form state:', updated); // Debug log
      return updated;
    });

    // Update selected category when category field changes
    if (field === 'category') {
      const category = categories.find(cat => cat._id === value);
      setSelectedCategory(category);
      // Reset subcategory when category changes
      setOfferForm(prev => ({ ...prev, subcategoryId: '' }));
    }
  };

  const handleCreateOffer = async () => {
    try {
      setOfferLoading(true);
      setOfferError('');

      // Client-side validation for required fields
      const validationErrors = [];

      if (!offerForm.title?.trim()) {
        validationErrors.push('Offer title is required');
      }

      if (!offerForm.description?.trim()) {
        validationErrors.push('Offer description is required');
      }

      if (!offerForm.purchaseUrl?.trim()) {
        validationErrors.push('Purchase URL is required');
      } else {
        // URL validation
        const url = offerForm.purchaseUrl.trim();
        try {
          new URL(url);
        } catch (error) {
          validationErrors.push('Purchase URL must be a valid URL (e.g., https://example.com/offer)');
        }
      }

      if (!offerForm.category) {
        validationErrors.push('Category is required');
      }

      if (!offerForm.subcategoryId) {
        validationErrors.push('Subcategory is required');
      }

      const isRevenueShare = offerForm.paysOn === 'Revenue Share';

      if (isRevenueShare) {
        const pct = parseFloat(offerForm.revenueSharePercent);
        if (offerForm.revenueSharePercent === '' || isNaN(pct) || pct < 0 || pct > 100) {
          validationErrors.push('Revenue Share % is required (0-100) when Pays on is Revenue Share');
        }
      } else {
        // Payout is now load-bearing, not just display text: the postback
        // endpoint derives the commission for Click/Conversion offers from
        // this field whenever the third party's postback doesn't include one
        // (the canonical postback URL for fixed offers never does), parsed
        // the same way the server parses it — strip everything but digits,
        // '.', and '-'. A non-numeric or missing value here means every real
        // postback for this offer will 400 with no payout to record.
        const cleaned = (offerForm.payout || '').replace(/[^0-9.\-]/g, '');
        const parsedPayout = parseFloat(cleaned);
        if (!offerForm.payout?.trim() || isNaN(parsedPayout)) {
          validationErrors.push('Payout is required (a numeric dollar amount, e.g. 50 or $50) when Pays on is Click or Conversion');
        }
      }

      if (validationErrors.length > 0) {
        setOfferError(validationErrors.join('\n'));
        setOfferLoading(false);
        return;
      }

      // Prepare offer data with proper formatting
      const offerData = {
        title: offerForm.title.trim(),
        description: offerForm.description.trim(),
        purchaseUrl: offerForm.purchaseUrl.trim(),
        category: offerForm.category,
        subcategoryId: offerForm.subcategoryId,
        expirationDate: offerForm.expirationDate instanceof Date ? offerForm.expirationDate.toISOString() : offerForm.expirationDate,
        published: Boolean(offerForm.published),
        payoutMethod: isRevenueShare ? 'revenue_share' : 'fixed',
        // Only send revenueSharePercent when it's actually the active
        // method — avoids persisting a stale % against a fixed-payout offer.
        ...(isRevenueShare
          ? { revenueSharePercent: parseFloat(offerForm.revenueSharePercent) }
          : {})
      };

      // Skip the free-text `payout` field entirely when Revenue Share is
      // selected — that field isn't shown/edited in this mode (the box shows
      // Revenue Share % instead), so sending it would persist a stale/
      // unrelated dollar string left over from a prior fixed-payout edit.
      // Required (validated above) and always sent when not revenue-share.
      if (!isRevenueShare) {
        offerData.payout = offerForm.payout.trim();
      }

      if (offerForm.typeOfAction?.trim()) {
        offerData.typeOfAction = offerForm.typeOfAction.trim();
      }

      if (offerForm.paysOn?.trim()) {
        offerData.paysOn = offerForm.paysOn.trim();
      }

      if (offerForm.problemSolved?.trim()) {
        offerData.problemSolved = offerForm.problemSolved.trim();
      }

      if (Array.isArray(offerForm.countriesAccepted) && offerForm.countriesAccepted.length > 0) {
        offerData.countriesAccepted = offerForm.countriesAccepted;
      }

      if (offerForm.trafficSources) {
        offerData.trafficSources = Array.isArray(offerForm.trafficSources) ?
          offerForm.trafficSources.join(', ') :
          offerForm.trafficSources.trim();
      }

      if (offerForm.trackingType?.trim()) {
        offerData.trackingType = offerForm.trackingType.trim();
      }

      if (offerForm.brand?.trim()) {
        offerData.brand = offerForm.brand.trim();
      } else if (editingOffer?.brand) {
        offerData.brand = editingOffer.brand;
      }

      console.log('Form data before processing:', offerForm); // Debug log
      console.log('Processed offer data to send:', offerData); // Debug log
      console.log('Editing offer ID:', editingOffer?._id); // Debug log
      console.log('All form fields:', {
        title: offerForm.title,
        payout: offerForm.payout,
        typeOfAction: offerForm.typeOfAction,
        paysOn: offerForm.paysOn,
        purchaseUrl: offerForm.purchaseUrl,
        description: offerForm.description,
        category: offerForm.category,
        subcategoryId: offerForm.subcategoryId,
        problemSolved: offerForm.problemSolved,
        countriesAccepted: offerForm.countriesAccepted,
        trafficSources: offerForm.trafficSources,
        trackingType: offerForm.trackingType,
        published: offerForm.published
      }); // Debug log

      if (editingOffer && editingOffer._id) {
        const result = await updateOffer(editingOffer._id, offerData);
        console.log('Update result:', result); // Debug log
        setSuccess('Offer updated successfully!');
        setEditOfferDialogOpen(false);
        setEditingOffer(null);

        // Clear brand offers cache to force refresh of expanded brand offers
        const brandId = editingOffer.brand?._id || editingOffer.brand;
        console.log('Attempting cache invalidation for brand:', {
          editingOfferBrand: editingOffer.brand,
          resolvedBrandId: brandId,
          expandedBrands: Array.from(expandedBrands),
          isExpanded: brandId ? expandedBrands.has(brandId) : false
        });

        if (brandId) {
          console.log('Clearing brand offers cache for brand:', brandId);
          setBrandOffers(prev => {
            const newCache = { ...prev, [brandId]: undefined };
            console.log('Updated brand offers cache:', newCache);
            return newCache;
          });

          // Always refresh if we have a brand ID, regardless of expansion state
          console.log('Refreshing brand offers for brand:', brandId);
          try {
            const { getOffersByBrandAdmin } = await import('../../services/offerService');
            const response = await getOffersByBrandAdmin(brandId, 1, 100, true);
            console.log('Fetched fresh brand offers:', response.offers);
            setBrandOffers(prev => ({
              ...prev,
              [brandId]: response.offers || []
            }));
          } catch (error) {
            console.error('Error refreshing brand offers:', error);
          }
        } else {
          console.log('No brand ID found for cache invalidation');
        }

        // Clear available offers cache to ensure fresh data in brand setup
        setAvailableOffers([]);

        // Reset form after successful update
        setOfferForm({
          title: '',
          payout: '',
          typeOfAction: '',
          paysOn: '',
          revenueSharePercent: '',
          purchaseUrl: '',
          description: '',
          category: '',
          subcategoryId: '',
          problemSolved: '',
          countriesAccepted: [],
          trafficSources: [],
          trackingType: '',
          expirationDate: NO_EXPIRY_DATE,
          published: true
        });
        setSelectedCategory(null);


      } else {
        await createOffer(offerData);
        setSuccess('Offer created successfully!');

        // Clear available offers cache to ensure fresh data in brand setup
        console.log('Clearing available offers cache after create...');
        setAvailableOffers([]);

        // Reset form
        setOfferForm({
          title: '',
          payout: '',
          typeOfAction: '',
          paysOn: '',
          revenueSharePercent: '',
          purchaseUrl: '',
          description: '',
          category: '',
          subcategoryId: '',
          problemSolved: '',
          countriesAccepted: [],
          trafficSources: [],
          trackingType: '',
          expirationDate: NO_EXPIRY_DATE,
          published: true
        });
        setSelectedCategory(null);


      }

      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      console.error('Error creating/updating offer:', err);
      console.error('Error details:', err.response?.data || err.message);
      setOfferError(editingOffer ? 'Failed to update offer. Please try again.' : 'Failed to create offer. Please try again.');
    } finally {
      setOfferLoading(false);
    }
  };

  const handleEditOffer = async (offer) => {
    console.log('Editing offer:', offer); // Debug log
    console.log('Offer brand info:', {
      brand: offer.brand,
      brandId: offer.brand?._id,
      brandType: typeof offer.brand
    }); // Debug brand info

    setEditingOffer(offer);

    // Fetch categories if not already loaded
    let currentCategories = categories;
    if (currentCategories.length === 0) {
      currentCategories = await fetchCategories();
    }

    const categoryId = offer.category?._id || offer.category;
    const category = currentCategories.find(cat => cat._id === categoryId);
    setSelectedCategory(category);

    setOfferForm({
      title: offer.title || '',
      payout: offer.payout || '',
      typeOfAction: offer.typeOfAction || '',
      // "Pays on" now doubles as the payout-method switch — if this offer is
      // configured as revenue-share, show that selected here regardless of
      // whatever raw value was previously stored in paysOn, so the form
      // reflects the single source of truth going forward.
      paysOn: offer.payoutMethod === 'revenue_share' ? 'Revenue Share' : (offer.paysOn || ''),
      revenueSharePercent: offer.revenueSharePercent ?? '',
      purchaseUrl: offer.purchaseUrl || '',
      description: offer.description || '',
      category: categoryId || '',
      subcategoryId: offer.subcategoryId || '',
      problemSolved: offer.problemSolved || '',
      countriesAccepted: offer.countriesAccepted || [],
      trafficSources: offer.trafficSources ? (Array.isArray(offer.trafficSources) ? offer.trafficSources : offer.trafficSources.split(',').map(s => s.trim()).filter(s => s)) : [],
      trackingType: offer.trackingType || '',
      brand: offer.brand?._id || offer.brand || '',
      expirationDate: offer.expirationDate ? new Date(offer.expirationDate) : NO_EXPIRY_DATE,
      published: offer.published !== undefined ? offer.published : true
    });

    console.log('Form populated with:', {
      title: offer.title,
      payout: offer.payout,
      category: categoryId,
      subcategoryId: offer.subcategoryId,
      problemSolved: offer.problemSolved,
      trafficSources: offer.trafficSources
    }); // Debug log

    setEditOfferDialogOpen(true);
  };

  const handleTogglePauseOffer = async (offer) => {
    const pausing = offer.published !== false;
    try {
      const updated = await setOfferPaused(offer._id, pausing);
      const brandId = offer.brand?._id || offer.brand;
      setBrandOffers(prev => ({
        ...prev,
        [brandId]: (prev[brandId] || []).map(o =>
          o._id === offer._id ? { ...o, published: updated.published } : o
        )
      }));
      setSuccess(pausing ? 'Offer paused' : 'Offer resumed');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error pausing/resuming offer:', error);
      setOfferError(`Error ${pausing ? 'pausing' : 'resuming'} offer. Please try again.`);
    }
  };

  const handleDeleteOfferClick = (offer) => {
    setOfferToDelete(offer);
    setDeleteOfferDialogOpen(true);
  };

  const handleDeleteOfferConfirm = async () => {
    try {
      setOfferLoading(true);
      await deleteOffer(offerToDelete._id);
      setSuccess('Offer deleted successfully!');

      // Clear brand offers cache to force refresh of expanded brand offers
      const brandId = offerToDelete.brand?._id || offerToDelete.brand;
      console.log('Attempting cache invalidation after delete for brand:', {
        offerToDeleteBrand: offerToDelete.brand,
        resolvedBrandId: brandId,
        expandedBrands: Array.from(expandedBrands)
      });

      if (brandId) {
        console.log('Clearing brand offers cache for brand after delete:', brandId);
        setBrandOffers(prev => ({
          ...prev,
          [brandId]: undefined
        }));

        // Always refresh if we have a brand ID
        console.log('Refreshing brand offers after delete for brand:', brandId);
        try {
          const { getOffersByBrandAdmin } = await import('../../services/offerService');
          const response = await getOffersByBrandAdmin(brandId, 1, 100, true);
          setBrandOffers(prev => ({
            ...prev,
            [brandId]: response.offers || []
          }));
        } catch (error) {
          console.error('Error refreshing brand offers after delete:', error);
        }
      }

      // Clear available offers cache to ensure fresh data in brand setup
      setAvailableOffers([]);

      setDeleteOfferDialogOpen(false);
      setOfferToDelete(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting offer:', error);
      setOfferError('Error deleting offer. Please try again.');
    } finally {
      setOfferLoading(false);
    }
  };

  const handleDeleteOfferCancel = () => {
    setDeleteOfferDialogOpen(false);
    setOfferToDelete(null);
  };

  const handleEditOfferCancel = () => {
    setEditOfferDialogOpen(false);
    setEditingOffer(null);
    // Reset form to prevent stale data
    setOfferForm({
      title: '',
      payout: '',
      typeOfAction: '',
      paysOn: '',
      revenueSharePercent: '',
      purchaseUrl: '',
      description: '',
      category: '',
      subcategoryId: '',
      problemSolved: '',
      countriesAccepted: [],
      trafficSources: [],
      trackingType: '',
      brand: '',
      expirationDate: NO_EXPIRY_DATE,
      published: true
    });
    setSelectedCategory(null);
  };



  const handleEditBrand = async (brand) => {
    setEditingBrand(brand);

    // Populate form with brand data
    setBrandForm({
      networkBrand: brand.networkBrand || '',
      brandName: brand.name || '',
      company: brand.company || '',
      contactName: brand.contactInfo?.email || '', // We'll use email as contact name for now
      contactEmail: brand.contactInfo?.email || '',
      addressField1: brand.headquarters?.address || '',
      addressField2: '',
      country: brand.headquarters?.country || '',
      stateProvince: brand.headquarters?.state || '',
      city: brand.headquarters?.city || '',
      websiteHomepage: brand.website || '',
      tiktok: brand.socialMedia?.tiktok || '',
      instagram: brand.socialMedia?.instagram || '',
      facebook: brand.socialMedia?.facebook || '',
      status: brand.isActive !== undefined ? brand.isActive : true
    });

    setEditDialogOpen(true);
  };

  const handleUpdateBrand = async () => {
    try {
      setLoading(true);

      // Client-side validation
      const validationErrors = [];

      // Validate website URL if provided
      if (brandForm.websiteHomepage && brandForm.websiteHomepage.trim()) {
        const websiteUrl = brandForm.websiteHomepage.trim();
        if (!isValidUrl(websiteUrl)) {
          validationErrors.push('Website must be a valid URL (e.g., https://example.com)');
        }
      }

      // Validate contact email if provided
      if (brandForm.contactEmail && brandForm.contactEmail.trim()) {
        const email = brandForm.contactEmail.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          validationErrors.push('Please provide a valid contact email address');
        }
      }

      // Validate social media URLs if provided
      ['tiktok', 'instagram', 'facebook'].forEach(platform => {
        const url = brandForm[platform];
        if (url && url.trim() && !url.startsWith('@') && !/^https?:\/\/.+/.test(url.trim())) {
          validationErrors.push(`${platform.charAt(0).toUpperCase() + platform.slice(1)} must be a valid URL or username starting with @`);
        }
      });

      // Show validation errors if any
      if (validationErrors.length > 0) {
        setLoading(false);
        alert('Validation Failed:\n\n' + validationErrors.join('\n'));
        return;
      }

      const brandData = {
        networkBrand: brandForm.networkBrand,
        name: brandForm.brandName,
        company: brandForm.company || undefined,
        industry: brandForm.country || 'General',
        website: brandForm.websiteHomepage || undefined,
        headquarters: {
          address: brandForm.addressField1,
          city: brandForm.city,
          state: brandForm.stateProvince,
          country: brandForm.country
        },
        socialMedia: {
          tiktok: brandForm.tiktok || undefined,
          instagram: brandForm.instagram || undefined,
          facebook: brandForm.facebook || undefined
        },
        isActive: brandForm.status
      };

      if (brandForm.contactEmail && brandForm.contactEmail.includes('@')) {
        brandData.contactInfo = {
          email: brandForm.contactEmail
        };
      }

      // Remove undefined values
      Object.keys(brandData).forEach(key => {
        if (brandData[key] === undefined) {
          delete brandData[key];
        }
      });

      await brandService.updateBrand(editingBrand._id, brandData);
      console.log('Updated brand:', editingBrand);

      setSuccess('Brand updated successfully!');
      setEditDialogOpen(false);
      setEditingBrand(null);
      setBrandForm({
        networkBrand: '',
        brandName: '',
        company: '',
        contactName: '',
        contactEmail: '',
        addressField1: '',
        addressField2: '',
        country: '',
        stateProvince: '',
        city: '',
        websiteHomepage: '',
        tiktok: '',
        instagram: '',
        facebook: '',
        status: true
      });
      fetchBrands();
      setTimeout(() => setSuccess(''), 3000);

    } catch (error) {
      console.error('Error updating brand:', error);
      setError('Error updating brand. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (brand) => {
    setBrandToDelete(brand);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      await brandService.deleteBrand(brandToDelete._id);
      setSuccess('Brand deleted successfully!');
      setDeleteDialogOpen(false);
      setBrandToDelete(null);
      fetchBrands();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting brand:', error);
      setError('Error deleting brand. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBrandToDelete(null);
  };

  const handleCreateBrand = async () => {
    try {
      setLoading(true);

      // Client-side validation
      const validationErrors = [];

      // Validate website URL if provided
      if (brandForm.websiteHomepage && brandForm.websiteHomepage.trim()) {
        const websiteUrl = brandForm.websiteHomepage.trim();
        if (!isValidUrl(websiteUrl)) {
          validationErrors.push('Website must be a valid URL (e.g., https://example.com)');
        }
      }

      // Validate contact email if provided
      if (brandForm.contactEmail && brandForm.contactEmail.trim()) {
        const email = brandForm.contactEmail.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          validationErrors.push('Please provide a valid contact email address');
        }
      }

      // Validate social media URLs if provided
      ['tiktok', 'instagram', 'facebook'].forEach(platform => {
        const url = brandForm[platform];
        if (url && url.trim() && !url.startsWith('@') && !/^https?:\/\/.+/.test(url.trim())) {
          validationErrors.push(`${platform.charAt(0).toUpperCase() + platform.slice(1)} must be a valid URL or username starting with @`);
        }
      });

      // Show validation errors if any
      if (validationErrors.length > 0) {
        setLoading(false);
        alert('Validation Failed:\n\n' + validationErrors.join('\n'));
        return;
      }

      // Create brand data object matching the API schema
      const brandData = {
        networkBrand: brandForm.networkBrand,
        name: brandForm.brandName,
        company: brandForm.company || undefined,
        industry: brandForm.country || 'General',
        website: brandForm.websiteHomepage || undefined,
        headquarters: {
          address: brandForm.addressField1,
          city: brandForm.city,
          state: brandForm.stateProvince,
          country: brandForm.country
        },
        socialMedia: {
          tiktok: brandForm.tiktok || undefined,
          instagram: brandForm.instagram || undefined,
          facebook: brandForm.facebook || undefined
        },
        isActive: brandForm.status
      };

      // Only add contactInfo if we have valid email data
      if (brandForm.contactEmail && brandForm.contactEmail.includes('@')) {
        brandData.contactInfo = {
          email: brandForm.contactEmail
        };
      }

      // Remove undefined values
      Object.keys(brandData).forEach(key => {
        if (brandData[key] === undefined) {
          delete brandData[key];
        }
      });

      // Call API to create brand
      const createdBrand = await brandService.createBrand(brandData);
      console.log('Created brand:', createdBrand);

      // Associate selected offers with the brand
      if (brandForm.selectedOffers && brandForm.selectedOffers.length > 0) {
        console.log(`Attempting to associate ${brandForm.selectedOffers.length} offers with brand ID: ${createdBrand._id}`);
        console.log('Selected offer IDs:', brandForm.selectedOffers);

        try {
          const { updateOffer } = await import('../../services/offerService');

          // Update each selected offer to associate it with the brand
          const updatePromises = brandForm.selectedOffers.map(async (offerId) => {
            console.log(`Updating offer ${offerId} with brand ${createdBrand._id}`);
            try {
              const result = await updateOffer(offerId, { brand: createdBrand._id });
              console.log(`Successfully updated offer ${offerId}:`, result);
              return result;
            } catch (individualOfferError) {
              console.error(`Failed to update offer ${offerId}:`, individualOfferError);
              throw individualOfferError;
            }
          });

          const results = await Promise.all(updatePromises);
          console.log(`Successfully associated ${results.length} offers with brand ${createdBrand.name}`);

          // Clear the brand offers cache to force refresh
          setBrandOffers(prev => ({
            ...prev,
            [createdBrand._id]: undefined
          }));

        } catch (offerUpdateError) {
          console.error('Error associating offers with brand:', offerUpdateError);
          alert(`Brand created successfully, but failed to associate some offers: ${offerUpdateError.message}`);
        }
      } else {
        console.log('No offers selected for association');
      }

      // Reset form after successful creation
      setBrandForm({
        networkBrand: '',
        brandName: '',
        company: '',
        contactName: '',
        contactEmail: '',
        addressField1: '',
        addressField2: '',
        country: '',
        stateProvince: '',
        city: '',
        websiteHomepage: '',
        tiktok: '',
        instagram: '',
        facebook: '',
        status: true,
        selectedOffers: []
      });

      // Show success message and switch to Offer Setup tab
      setSuccess('Brand created successfully!');
      setActiveTab(2); // Switch to Offer Setup tab
      fetchBrands(); // Reload brands from API
      setTimeout(() => setSuccess(''), 3000);

    } catch (error) {
      console.error('Error creating brand:', error);
      setError('Error creating brand. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };
  const contextValue = {
    user, navigate,
    activeTab, setActiveTab,
    brandForm, setBrandForm,
    existingBrands, setExistingBrands,
    loading, setLoading,
    error, setError,
    success, setSuccess,
    editingBrand, setEditingBrand,
    editDialogOpen, setEditDialogOpen,
    deleteDialogOpen, setDeleteDialogOpen,
    brandToDelete, setBrandToDelete,
    offerLoading, setOfferLoading,
    offerError, setOfferError,
    categories, setCategories,
    selectedCategory, setSelectedCategory,
    offerForm, setOfferForm,
    editingOffer, setEditingOffer,
    editOfferDialogOpen, setEditOfferDialogOpen,
    deleteOfferDialogOpen, setDeleteOfferDialogOpen,
    offerToDelete, setOfferToDelete,
    availableOffers, setAvailableOffers,
    availableOffersLoading, setAvailableOffersLoading,
    expandedBrands, setExpandedBrands,
    brandOffers, setBrandOffers,
    sharedElements, setSharedElements,
    sharedSelectedElement, setSharedSelectedElement,
    sharedSelectedElementContext, setSharedSelectedElementContext,
    sharedSelectedCategory, setSharedSelectedCategory,
    sharedSelectedSubtype, setSharedSelectedSubtype,
    sharedSelectedBrand, setSharedSelectedBrand,
    sharedSelectedOffer, setSharedSelectedOffer,
    sharedPages, setSharedPages,
    sharedSelectedTemplateId, setSharedSelectedTemplateId,
    fetchBrands, fetchCategories, fetchAvailableOffers,
    handleToggleBrandOffers, handleTabChange, handleOfferFormChange,
    handleCreateOffer, handleEditOffer, handleDeleteOfferClick, handleTogglePauseOffer,
    handleDeleteOfferConfirm, handleDeleteOfferCancel,
    handleEditOfferCancel, handleEditBrand, handleUpdateBrand,
    handleDeleteClick, handleDeleteConfirm, handleDeleteCancel,
    handleCreateBrand,
    isValidUrl,
  };

  return (
    <BrandsContext.Provider value={contextValue}>
    <DashboardLayout hideHeader={activeTab === 3}>
      <Container maxWidth="xl" sx={{ pt: activeTab === 3 ? 0 : 1 }}>
        {activeTab !== 3 && (
          <Typography variant="h5" sx={{ mb: 2 }}>
            Brand & Offer Management
          </Typography>
        )}

        {/* Success/Error Messages */}
        <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')}>
          <Alert severity="success">{success}</Alert>
        </Snackbar>
        <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError('')}>
          <Alert severity="error">{error}</Alert>
        </Snackbar>

        {/* Main Content */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{ 
              minHeight: activeTab === 3 ? 32 : 48,
              '& .MuiTab-root': {
                minHeight: activeTab === 3 ? 32 : 48,
                fontSize: activeTab === 3 ? '0.75rem' : '0.875rem',
                py: activeTab === 3 ? 0 : 1.5
              }
            }}
          >
            <Tab label="Brand List" />
            <Tab label="Brand Setup" />
            <Tab label="Offer Setup" />
            <Tab label="Offer Element Setup" />
          </Tabs>
        </Box>

        {activeTab === 0 && <BrandListTab />}
        {activeTab === 1 && <BrandSetupTab />}
        {activeTab === 2 && <OfferSetupTab />}
        {activeTab === 3 && <OfferElementSetupTab />}

        {/* Edit Brand Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Edit Brand</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="Network / Brand"
                  fullWidth
                  value={brandForm.networkBrand}
                  onChange={(e) => setBrandForm({ ...brandForm, networkBrand: e.target.value })}
                  placeholder="e.g., Bill pay CO"
                  helperText="Enter the network or brand name"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Brand Name"
                  fullWidth
                  value={brandForm.brandName}
                  onChange={(e) => setBrandForm({ ...brandForm, brandName: e.target.value })}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Company"
                  fullWidth
                  value={brandForm.company}
                  onChange={(e) => setBrandForm({ ...brandForm, company: e.target.value })}
                  placeholder="Enter company name"
                  helperText="Optional - company or organization name"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Contact Name"
                  fullWidth
                  value={brandForm.contactName}
                  onChange={(e) => setBrandForm({ ...brandForm, contactName: e.target.value })}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Contact Email"
                  type="email"
                  fullWidth
                  value={brandForm.contactEmail}
                  onChange={(e) => setBrandForm({ ...brandForm, contactEmail: e.target.value })}
                  placeholder="contact@company.com"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Address"
                  fullWidth
                  value={brandForm.addressField1}
                  onChange={(e) => setBrandForm({ ...brandForm, addressField1: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={brandForm.country}
                    label="Country"
                    onChange={(e) => setBrandForm({ ...brandForm, country: e.target.value })}
                  >
                    <MenuItem value="US">United States</MenuItem>
                    <MenuItem value="CA">Canada</MenuItem>
                    <MenuItem value="UK">United Kingdom</MenuItem>
                    <MenuItem value="AU">Australia</MenuItem>
                    <MenuItem value="DE">Germany</MenuItem>
                    <MenuItem value="FR">France</MenuItem>
                    <MenuItem value="JP">Japan</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="State/Province"
                  fullWidth
                  value={brandForm.stateProvince}
                  onChange={(e) => setBrandForm({ ...brandForm, stateProvince: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="City"
                  fullWidth
                  value={brandForm.city}
                  onChange={(e) => setBrandForm({ ...brandForm, city: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Website"
                  fullWidth
                  type="url"
                  value={brandForm.websiteHomepage}
                  onChange={(e) => setBrandForm({ ...brandForm, websiteHomepage: e.target.value })}
                  placeholder="https://example.com"
                  helperText="Must include http:// or https:// (e.g., https://onnit.com)"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="TikTok"
                  fullWidth
                  value={brandForm.tiktok}
                  onChange={(e) => setBrandForm({ ...brandForm, tiktok: e.target.value })}
                  placeholder="@username or profile URL"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Instagram"
                  fullWidth
                  value={brandForm.instagram}
                  onChange={(e) => setBrandForm({ ...brandForm, instagram: e.target.value })}
                  placeholder="@username or profile URL"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Facebook"
                  fullWidth
                  value={brandForm.facebook}
                  onChange={(e) => setBrandForm({ ...brandForm, facebook: e.target.value })}
                  placeholder="Page name or URL"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="edit-offers-select-label">Associate Offers</InputLabel>
                  <Select
                    labelId="edit-offers-select-label"
                    id="edit-offers-select"
                    multiple
                    value={brandForm.selectedOffers || []}
                    onChange={(e) => setBrandForm({ ...brandForm, selectedOffers: e.target.value })}
                    label="Associate Offers"
                    disabled={availableOffersLoading}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const offer = availableOffers.find(o => o._id === value);
                          return (
                            <Chip
                              key={value}
                              label={offer?.title || value}
                              size="small"
                              sx={{ maxWidth: 200 }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {availableOffersLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading offers...
                      </MenuItem>
                    ) : availableOffers.length === 0 ? (
                      <MenuItem disabled>
                        No offers available
                      </MenuItem>
                    ) : (
                      availableOffers.map((offer) => (
                        <MenuItem key={offer._id} value={offer._id}>
                          {offer.title}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Select offers to associate with this brand. You can select multiple offers.
                  </Typography>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={brandForm.status}
                      onChange={(e) => setBrandForm({ ...brandForm, status: e.target.checked })}
                    />
                  }
                  label="Active Brand"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateBrand} variant="contained">Update Brand</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the brand "{brandToDelete?.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Offer Dialog */}
        <Dialog open={editOfferDialogOpen} onClose={handleEditOfferCancel} maxWidth="md" fullWidth>
          <DialogTitle>
            Edit Offer
            {process.env.NODE_ENV === 'development' && (
              <Typography variant="caption" display="block">
                ID: {editingOffer?._id} | Title: "{offerForm.title}" | Payout: "{offerForm.payout}"
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Offer Name"
                  value={offerForm.title}
                  onChange={handleOfferFormChange('title')}
                  fullWidth
                  required
                  placeholder="Enter offer name"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Type of Action</InputLabel>
                  <Select
                    value={offerForm.typeOfAction}
                    onChange={handleOfferFormChange('typeOfAction')}
                  >
                    <MenuItem value="download">download</MenuItem>
                    <MenuItem value="lead">lead</MenuItem>
                    <MenuItem value="sale">sale</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Pays on</InputLabel>
                  <Select
                    value={offerForm.paysOn}
                    onChange={handleOfferFormChange('paysOn')}
                  >
                    <MenuItem value="Click">Click</MenuItem>
                    <MenuItem value="Conversion">Conversion</MenuItem>
                    <MenuItem value="Revenue Share">Revenue Share</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Same visual field either way — what it holds and how it's
                  validated switches based on "Pays on". Selecting Revenue
                  Share here is what actually flips Offer.payoutMethod to
                  'revenue_share' server-side; see handleCreateOffer below. */}
              <Grid item xs={12} md={6}>
                {offerForm.paysOn === 'Revenue Share' ? (
                  <TextField
                    label="Revenue Share %"
                    type="number"
                    value={offerForm.revenueSharePercent}
                    onChange={handleOfferFormChange('revenueSharePercent')}
                    fullWidth
                    required
                    placeholder="e.g., 10"
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    helperText="% of the total sale amount the brand reports to us that we're paid — e.g. 10 for 10%"
                    error={offerError && offerError.includes('Revenue Share')}
                  />
                ) : (
                  <TextField
                    label="Payout"
                    value={offerForm.payout}
                    onChange={handleOfferFormChange('payout')}
                    fullWidth
                    required
                    placeholder="e.g., 50 or $50"
                    helperText="Numeric commission per conversion — used server-side when the advertiser's postback doesn't include one (the standard postback URL for this doesn't)"
                    error={offerError && offerError.includes('Payout is required')}
                  />
                )}
              </Grid>


              <Grid item xs={12}>
                <TextField
                  label="Offer URL *"
                  value={offerForm.purchaseUrl}
                  onChange={handleOfferFormChange('purchaseUrl')}
                  fullWidth
                  required
                  placeholder="https://example.com/offer"
                  error={
                    (offerError && offerError.includes('Purchase URL')) ||
                    (offerForm.purchaseUrl && offerForm.purchaseUrl.trim() && !isValidUrl(offerForm.purchaseUrl.trim()))
                  }
                  helperText={
                    offerError && offerError.includes('Purchase URL')
                      ? 'Purchase URL is required'
                      : offerForm.purchaseUrl && offerForm.purchaseUrl.trim() && !isValidUrl(offerForm.purchaseUrl.trim())
                        ? 'Please enter a valid URL (e.g., https://example.com/offer)'
                        : ''
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={existingBrands}
                  getOptionLabel={(option) => option.name || ''}
                  value={existingBrands.find(brand => brand._id === offerForm.brand) || null}
                  onChange={(event, newValue) => {
                    setOfferForm(prev => ({
                      ...prev,
                      brand: newValue ? newValue._id : ''
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Brand"
                      placeholder="Search brands..."
                      helperText="Select the brand this offer is associated with"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body1">{option.name}</Typography>
                        {option.company && (
                          <Typography variant="caption" color="text.secondary">
                            {option.company}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  clearOnBlur={false}
                  blurOnSelect={true}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Company"
                  value={existingBrands.find(brand => brand._id === offerForm.brand)?.company || ''}
                  fullWidth
                  disabled
                  placeholder="Company name will appear when brand is selected"
                  helperText="Company name from selected brand"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description *"
                  value={offerForm.description}
                  onChange={handleOfferFormChange('description')}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Describe the offer details"
                  required
                  error={offerError && offerError.includes('description')}
                  helperText={offerError && offerError.includes('description') ? 'Description is required' : ''}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={offerError && offerError.includes('Category')}>
                  <InputLabel>Category *</InputLabel>
                  <Select
                    value={offerForm.category}
                    onChange={handleOfferFormChange('category')}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {offerError && offerError.includes('Category') && (
                    <FormHelperText error>Category is required</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required disabled={!selectedCategory} error={offerError && offerError.includes('Subcategory')}>
                  <InputLabel>Sub Category *</InputLabel>
                  <Select
                    value={offerForm.subcategoryId}
                    onChange={handleOfferFormChange('subcategoryId')}
                  >
                    {selectedCategory?.subcategories?.map((subcategory) => (
                      <MenuItem key={subcategory._id} value={subcategory._id}>
                        {subcategory.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {offerError && offerError.includes('Subcategory') && (
                    <FormHelperText error>Subcategory is required</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Problem Solved</InputLabel>
                  <Select
                    value={offerForm.problemSolved}
                    onChange={handleOfferFormChange('problemSolved')}
                    label="Problem Solved"
                  >
                    <MenuItem value="">
                      <em>Select a problem this offer solves</em>
                    </MenuItem>
                    <MenuItem value="gut pain">Gut Pain</MenuItem>
                    <MenuItem value="headache">Headache</MenuItem>
                    <MenuItem value="stress reduction">Stress Reduction</MenuItem>
                    <MenuItem value="can't sleep">Can't Sleep</MenuItem>
                    <MenuItem value="better sleep">Better Sleep</MenuItem>
                    <MenuItem value="focus">Focus</MenuItem>
                    <MenuItem value="back pain">Back Pain</MenuItem>
                  </Select>
                  <FormHelperText>Select the main problem this offer helps solve</FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Countries Accepted</InputLabel>
                  <Select
                    multiple
                    value={offerForm.countriesAccepted}
                    onChange={handleOfferFormChange('countriesAccepted')}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const countryNames = {
                            'Global': 'Global (All Countries)',
                            'US': 'United States',
                            'CA': 'Canada',
                            'UK': 'United Kingdom',
                            'AU': 'Australia',
                            'DE': 'Germany',
                            'FR': 'France',
                          };
                          return (
                            <Chip
                              key={value}
                              label={countryNames[value] || value}
                              onDelete={(event) => {
                                event.stopPropagation();
                                const newCountries = offerForm.countriesAccepted.filter(country => country !== value);
                                setOfferForm(prev => ({ ...prev, countriesAccepted: newCountries }));
                              }}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    <MenuItem value="Global">Global (All Countries)</MenuItem>
                    <MenuItem value="US">United States</MenuItem>
                    <MenuItem value="CA">Canada</MenuItem>
                    <MenuItem value="UK">United Kingdom</MenuItem>
                    <MenuItem value="AU">Australia</MenuItem>
                    <MenuItem value="DE">Germany</MenuItem>
                    <MenuItem value="FR">France</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="traffic-sources-label">Traffic Sources Allowed</InputLabel>
                  <Select
                    labelId="traffic-sources-label"
                    label="Traffic Sources Allowed"
                    value={offerForm.trafficSources ? (Array.isArray(offerForm.trafficSources) ? offerForm.trafficSources : offerForm.trafficSources.split(',').map(s => s.trim()).filter(s => s)) : []}
                    onChange={(event) => setOfferForm(prev => ({ ...prev, trafficSources: event.target.value }))}
                    multiple
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="Paid Search">Paid Search</MenuItem>
                    <MenuItem value="Google Search">Google Search</MenuItem>
                    <MenuItem value="TikTok Unpaid">TikTok Unpaid</MenuItem>
                    <MenuItem value="Instagram Unpaid">Instagram Unpaid</MenuItem>
                    <MenuItem value="Facebook Unpaid">Facebook Unpaid</MenuItem>
                    <MenuItem value="Meta Ads">Meta Ads</MenuItem>
                    <MenuItem value="TikTok Ads">TikTok Ads</MenuItem>
                    <MenuItem value="Mobile Ads">Mobile Ads</MenuItem>
                    <MenuItem value="Pops">Pops</MenuItem>
                    <MenuItem value="Native Ads">Native Ads</MenuItem>
                    <MenuItem value="Display Ads">Display Ads</MenuItem>
                  </Select>
                  <FormHelperText>Select which traffic sources are allowed for this offer</FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Tracking Type</InputLabel>
                  <Select
                    value={offerForm.trackingType}
                    onChange={handleOfferFormChange('trackingType')}
                  >
                    <MenuItem value="affiliate network">Affiliate Network</MenuItem>
                    <MenuItem value="server to server">Server to Server</MenuItem>
                    <MenuItem value="pixel">Pixel</MenuItem>
                  </Select>
                  <FormHelperText>Select the tracking method for this offer</FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={offerForm.published}
                      onChange={(e) => {
                        console.log('Switch changed to:', e.target.checked);
                        setOfferForm(prev => ({ ...prev, published: e.target.checked }));
                      }}
                    />
                  }
                  label="Published"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditOfferCancel}>Cancel</Button>
            <Button onClick={handleCreateOffer} variant="contained" disabled={offerLoading}>
              {offerLoading ? 'Updating...' : 'Update Offer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Offer Confirmation Dialog */}
        <Dialog open={deleteOfferDialogOpen} onClose={handleDeleteOfferCancel}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the offer "{offerToDelete?.title}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteOfferCancel}>Cancel</Button>
            <Button onClick={handleDeleteOfferConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
    </BrandsContext.Provider>
  );
};

export default Brands;
